import { serverTimestamp, type Timestamp } from "firebase/firestore";
import React from "react";
import { availablePlanFlowTemplates } from "../../templates/generated/allFlows";
import { PlanTemplateType } from "../../templates/generated/template_types";
import type {
  PlaniraTemplate,
  PlaniraTemplateModule,
} from "../../templates/types";
import { ModuleType, PlanQuestionType } from "../../templates/types";
import { useApi } from "../core/apis";
import { scrollToTop } from "../core/screens/types";
import { showConfetti } from "../core/states.ts/showConfetti";
import type BaseModuleModel from "../modules/core/BaseModuleModel";
import type {
  ModuleModelData,
  Snapshot,
} from "../modules/core/BaseModuleModel";
import { getDefaultSettingsForModule } from "../modules/utils";
import { defaultPlanSettings, type PlanModelData } from "../plans/PlanModel";
import { usePlanNavigation } from "../plans/usePlanNavigation";
import { EmptyTemplate } from "./EmptyTemplate";
import { HomeRenovationTemplate } from "./HomeRenovationTemplate";
import { PartyTimeTemplate } from "./PartyTimeTemplate";
import { QuickstartDatesTemplate } from "./QuickstartDatesTemplate";
import { QuickstartExpensesTemplate } from "./QuickstartExpensesTemplate";
import { QuickstartLocationsTemplate } from "./QuickstartLocationsTemplate";
import { QuickstartPollTemplate } from "./QuickstartPollTemplate";
import { QuickstartRsvpTemplate } from "./QuickstartRsvpTemplate";
import { TravelTimeTemplate } from "./TravelTimeTemplate";

export const availablePlanQuickstartTemplates: Partial<{
  [key in PlanTemplateType]: PlaniraTemplate;
}> = {
  [PlanTemplateType.QuickstartDates]: QuickstartDatesTemplate,
  [PlanTemplateType.QuickstartLocations]: QuickstartLocationsTemplate,
  [PlanTemplateType.QuickstartRsvp]: QuickstartRsvpTemplate,
  [PlanTemplateType.QuickstartPoll]: QuickstartPollTemplate,
  [PlanTemplateType.QuickstartExpenses]: QuickstartExpensesTemplate,
};

export const availablePlanTemplates: Partial<{
  [key in PlanTemplateType]: PlaniraTemplate;
}> = {
  [PlanTemplateType.TravelTime]: TravelTimeTemplate,
  [PlanTemplateType.PartyTime]: PartyTimeTemplate,
  [PlanTemplateType.HomeRenovation]: HomeRenovationTemplate,
  [PlanTemplateType.FromScratch]: EmptyTemplate,
};

export function getTemplateFromType(planTemplate: PlanTemplateType) {
  return (
    planTemplate &&
    (availablePlanTemplates[planTemplate] ||
      availablePlanQuickstartTemplates[planTemplate] ||
      availablePlanFlowTemplates[planTemplate])
  );
}

export function getQuestionForTemplateAndModule(
  t: PlanTemplateType,
  mType: ModuleType,
) {
  const template =
    availablePlanTemplates[t] || availablePlanQuickstartTemplates[t];

  if (!template) {
    return;
  }

  return template.sections[1].modules.find(
    (moduleTemplate) => moduleTemplate.type === mType,
  )?.questionOption?.question;
}

type AfterUserJoin = {
  trigger: (
    module: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>,
  ) => void;
  module: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>;
};

export function useCreatePlanFromTemplate() {
  const apis = useApi();
  const navigation = usePlanNavigation();

  return React.useCallback(
    async (
      template: PlaniraTemplate,
      modules: PlaniraTemplateModule[],
      title: string,
      userDisplayName?: string,
      skipNavigation?: boolean,
      planProps?: Partial<PlanModelData>,
    ) => {
      const plan = await apis.plansApi.add({
        type: template.planType,
        businessPlanUid: null,
        template: template.type,
        title: title ?? template.title,
        onTemplateComplete: template.onTemplateComplete ?? null,
        ts: serverTimestamp() as Timestamp,
        userIds: [], // TODO - add your own id?
        sectionIds: [],
        ghostUsers: [],
        joinOptions: {},
        archive: {
          moduleIds: [],
          userIds: [],
          sectionIds: [],
        },
        settings: defaultPlanSettings,
        progress: {
          rating: 0,
        },
        dev: { skipped: false, starred: false },
        tags: [],
        cache: {},
        ...planProps,
      });

      const moduleUids: string[] = [];

      const triggerAfterJoined: AfterUserJoin[] = [];

      for (let i = 0; i < template.sections.length; i++) {
        const s = template.sections[i];

        const section = await plan.addSection({
          title: s.section.title ?? "",
          isDraggable: s.section.isDraggable ?? true,
          isExpandable: s.section.isExpandable ?? true,
          attractState: s.section.attractState,
        });

        let modulesToCreate = s.modules;
        // Use modules from finetune if denfined
        if (i === 1 && modules !== undefined && modules.length > 0) {
          modulesToCreate = modules;
        }

        for (const m of modulesToCreate) {
          const shouldUsePlanTitle = m.type === ModuleType.PlanInfo;
          if (shouldUsePlanTitle || m.keep) {
            const moduleTitle = shouldUsePlanTitle ? title : m.title;

            const afterCreate = m.onCreated;

            const defaultData = m.defaultData ?? {};

            delete m.onCreated;
            delete m.defaultData;

            const createdModule = await section.addModule(
              {
                ...m,
                settings: getDefaultSettingsForModule(m.type, m.settings),
                completeAction: m.completeAction ?? null,
                questionType:
                  m.questionOption?.questionType ?? PlanQuestionType.None,
                title: moduleTitle,
                initiated: true,
                isDraggable: m.isDraggable ?? true,
              },
              { skipActivity: true, defaultData },
            );
            moduleUids.push(createdModule.uid);

            if (afterCreate) {
              triggerAfterJoined.push({
                trigger: afterCreate,
                module: createdModule,
              });
            }
          }
        }
      }

      if (userDisplayName) {
        await plan.joinWithCurrentUser(userDisplayName, false, moduleUids);
      }

      if (triggerAfterJoined.length) {
        await Promise.all(
          triggerAfterJoined.map(async (a) => {
            a.trigger(a.module);
          }),
        );
      }

      showConfetti();

      if (!skipNavigation) {
        navigation.navigate("Plan", {
          planUid: plan.uid,
        });

        scrollToTop();
      }

      return plan;
    },
    [apis, navigation],
  );
}
